import React, { useEffect, useRef } from 'react'
import { Button, Container, Modal, Typography } from '@cleartrip/ct-design-components'
import Image from 'next/image'

import { Cross } from '@/assets/icons'
import EliteBenefitsGrid from '@/components/EliteAfterBooking/EliteBenefitsGrid'
import { ELITE_MEMBERSHIP_ASSETS } from '@/constants/components/eliteMembership'

import styles from './EliteUnlockSoonModal.module.scss'

const BG_BLOB_SRC = 'https://rukmini-ct.flixcart.com/f_auto,q_auto/offermgmt-prod/offermgmt/images/earned_loyalty/elite-benefits-modal-bg.png'
const PERCENT_FRAME_SRC = 'https://rukmini-ct.flixcart.com/f_auto,q_auto/offermgmt-prod/offermgmt/images/earned_loyalty/Percent-Frame.png'
const STAR_SPARKLE_SRC = 'https://rukmini-ct.flixcart.com/f_auto,q_auto/offermgmt-prod/offermgmt/images/earned_loyalty/star-sparkle.png'
const VIEW_ALL_BENEFITS_BUTTON_TEXT = 'Explore Elite'

export type EliteUnlockSoonModalProps = {
    open: boolean
    onClose: () => void
    showViewAllBenefitsButton?: boolean
    onLoad?: () => void
    onViewAllBenefitsClick?: () => void
    onDismiss?: () => void
}

const EliteUnlockSoonModal: React.FC<EliteUnlockSoonModalProps> = ({
    open,
    onClose,
    showViewAllBenefitsButton = true,
    onLoad,
    onViewAllBenefitsClick,
    onDismiss,
}) => {
    // so "View all benefits" doesn't also fire a dismiss
    const viewClickedRef = useRef(false)

    useEffect(() => {
        if (open) {
            viewClickedRef.current = false
            onLoad?.()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const handleDismiss = () => {
        if (!viewClickedRef.current) {
            onDismiss?.()
        }
        onClose()
    }

    const handleViewAllBenefitsClick = () => {
        viewClickedRef.current = true
        onViewAllBenefitsClick?.()
        onClose()
        if (typeof window === 'undefined') return
        window.location.href = `${window.location.origin}/loyalty`
    }

    return (
        <Modal
            open={open}
            onClose={handleDismiss}
            hideCrossIcon
            allowBackdropClose
            modalContentCss={{ width: '476px', padding: '0', overflow: 'hidden', borderRadius: '16px' }}
            rootStyles={{ zIndex: 9999 }}
        >
            <Container className={`${styles.root} p-relative o-hidden pt-12 px-4 pb-7`}>
                <img className={`${styles.bgBlob} p-absolute r-0`} src={BG_BLOB_SRC} alt="" aria-hidden />
                <img className={`${styles.percentFrame} p-absolute l-0 t-0`} src={PERCENT_FRAME_SRC} alt="" aria-hidden />
                <img className={`${styles.star} p-absolute`} src={STAR_SPARKLE_SRC} alt="" aria-hidden />

                <Container
                    className={`${styles.closeBtn} p-absolute flex flex-middle flex-center c-pointer`}
                    onClick={handleDismiss}
                    role="button"
                    aria-label="Close"
                >
                    <Cross width={24} height={24} />
                </Container>
                <Container className={`${styles.inner} p-relative flex flex-column flex-middle`}>
                    <Container className='flex flex-column flex-middle flex-gap-3'>
                        <Image
                            className={styles.eliteWordmark}
                            src={ELITE_MEMBERSHIP_ASSETS.elite}
                            alt="Elite"
                            width={121}
                            height={42}
                        />
                    </Container>

                    <Container className={`${styles.benefitsRow} p-relative w-100p flex flex-middle flex-center`}>
                        <EliteBenefitsGrid />
                    </Container>

                    {showViewAllBenefitsButton && (
                        <Button minWidth='300px' className='mt-5' size="medium" onClick={handleViewAllBenefitsClick}>
                            {VIEW_ALL_BENEFITS_BUTTON_TEXT}
                        </Button>
                    )}
                </Container>
            </Container>
        </Modal>
    )
}

export default EliteUnlockSoonModal
